/**
 * WHAT YOU HAVE ACTUALLY DONE TODAY.
 *
 * Anthony: "Give a task completion counter for the desk on how many
 * actions they completed during the day that counts up as they go. Not
 * annoying, just on the desk main page."
 *
 * ─── WHAT COUNTS AS A THING DONE ───────────────────────────────────────
 *
 * Only actions that MOVED WORK and that the database can attribute to a
 * person. Moving a job along, recording a yes, taking a payment, adding a
 * photo, raising a job, clearing somebody off the front desk feed, and
 * TALKING TO A CUSTOMER, which is most of what a front desk does all day
 * and for a while counted for nothing at all.
 *
 * Reading a screen is not an action. Neither is anything a scheduled task
 * did on its own — every one of these sources carries the user id of the
 * person who pressed the button, and a server task leaves it null, so the
 * count is people rather than machinery.
 *
 * ─── WHY IT WILL LOOK LOW ON THE FIRST DAY, HONESTLY ───────────────────
 *
 * `actor_id` on a status change only started being written when that code
 * shipped, and the other sources only count from the moment somebody uses
 * them. This counts today, so history does not matter — but nobody should
 * read a low number on day one as a slow day.
 *
 * ─── AND IT DOES NOT NAG ───────────────────────────────────────────────
 *
 * No target, no bar to fill, no color that changes when it is low. A
 * counter that judges is a counter people learn to game, and the shop
 * already has one number that matters and it is the money. This is a
 * quiet tally of a day's work, which is a thing worth seeing at six
 * o'clock and nothing more.
 */

import { db } from "./client.ts";
import { shopDayStartIso } from "../ui/shopclock.ts";

export type DayTally = {
  total: number;
  /** Broken down, so the number can be believed rather than taken on trust. */
  parts: Array<{ what: string; n: number }>;
};

export async function myDay(): Promise<DayTally> {
  const sb = await db();
  const { data: me } = await sb.auth.getUser();
  const uid = me.user?.id;
  if (!uid) return { total: 0, parts: [] };

  // Midnight where the SHOP is, not where the browser is, and not where
  // Supercanic is either. A technician on a phone that has not caught up
  // after a drive should still see the shop's day; a Denver shop's day
  // should not roll over at eleven the night before.
  const from = shopDayStartIso();

  /**
   * A SILENT ZERO IS WORSE THAN A LOUD ONE.
   *
   * This used to discard the error and return 0, and for months it asked
   * job_authorizations for a `created_at` that does not exist -- the
   * column is `authorized_at`. PostgREST answered 42703 every time, the
   * error went in the bin, and recording a customer's yes scored nothing
   * on anybody's day. A wrong number that looks like a real one is the
   * only kind of counter worth not having.
   *
   * A table this login genuinely cannot read is still a quiet zero. That
   * is a permission, not a mistake. Everything else gets said out loud in
   * the console so the next wrong column has half a chance of being
   * noticed.
   */
  const count = async (
    table: string, column: string, dateCol: string,
  ): Promise<number> => {
    const { count: n, error } = await sb.from(table)
      .select("id", { count: "exact", head: true })
      .eq(column, uid).gte(dateCol, from);
    if (error) {
      // 42501 is "not allowed to look", which is fine and expected for
      // some roles. Anything else is this code being wrong.
      if (error.code !== "42501") {
        console.error(`myDay: ${table}.${column} by ${dateCol} —`, error.message);
      }
      return 0;
    }
    return n ?? 0;
  };

  const [moved, authed, paid, shot, cleared, raised, spoke] = await Promise.all([
    count("job_events", "actor_id", "created_at"),
    // `authorized_at`, not `created_at`. There is no created_at on this
    // table and asking for one scored every recorded yes as zero.
    count("job_authorizations", "recorded_by", "authorized_at"),
    count("payments", "recorded_by", "received_at"),
    count("photos", "taken_by", "created_at"),
    count("notifications", "cleared_by", "cleared_at"),
    count("jobs", "created_by", "created_at"),
    // EVERY CONTACT, BY WHOEVER MADE IT. Automations leave staff_id null,
    // so a night of textbacks does not land on the closer's tally.
    count("comm_events", "staff_id", "occurred_at"),
  ]);

  const parts = [
    { what: "contacted", n: spoke },
    { what: "moved along", n: moved },
    { what: "raised", n: raised },
    { what: "authorized", n: authed },
    { what: "paid", n: paid },
    { what: "photographed", n: shot },
    { what: "cleared", n: cleared },
  ].filter((p) => p.n > 0);

  return { total: parts.reduce((t, p) => t + p.n, 0), parts };
}
